import { type ContractDetailsData } from "~/features/contracts/redux/types";
import { type ProductDataDTO } from "~/domains/product/types/ProductDataDTO";

import { type PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";
import * as PriceUtils from "~/utils/PriceUtils";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { transformAddress } from "~/features/cart/cart/transformers/CheckoutViewDataTransformer";
import { getDate } from "~/common/utils/ApiHelper";
import { enhanceWithCache } from "~/common/utils/apiCache";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";

interface SDDetailsData {
  poNumber: string | null; // customer contract number
  soNumber: string | null; // schaeffler contract number
  contractValidFrom: number | null;
  contractValidTo: number | null;
  shipToAddress: AddressDTO | null;
  orderEntries: SDEntryData[] | null;
  datePlaced: number | null;
}

export interface SDEntryData {
  lineNumber: number | null; // position
  product: ProductDataDTO;
  contractConfirmedLines: ContractConfirmedLinesData[] | null;
  contractDetailsUrl: string;
  packCode: string;
  packSize: number;
  nettoPrice: PriceDataDTO | null;
  priceForTotal: PriceDataDTO | null;
  contractOpenQuantity: number | null;
  quantityRequested: number | null;
}

interface ContractConfirmedLinesData {
  confirmedDeliveryDate: number | null;
  confirmedQuantityValue: number | null;
}

const fetchContract = async (contractNo: string, materialNo: string, packCode: string) => {
  const apiUrl = resolveApiUrl(`/my-account/contract/${contractNo}/${materialNo}/${packCode}`);

  const response = await fetch(apiUrl, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });
  const data = await response.json();
  const transformed = {
    ...data,
    contractValidFrom: getDate(data.contractValidFrom),
    contractValidTo: getDate(data.contractValidTo),
    nextDisposition: getDate(data.nextDisposition),
    datePlaced: getDate(data.datePlaced),
    product: transformProduct(data.product),
  };
  return transformed as ContractDetailsResponse;
};

const cachedFetchContract = enhanceWithCache(
  fetchContract,
  (contractNo, materialNo, packCode) => `${contractNo}-${materialNo}-${packCode}`,
);

function fetchData(contractNo: string, materialNo: string, packCode: string, contractPosition: number | null) {
  return cachedFetchContract(contractNo, materialNo, packCode);
}

export const getContractDetails = enhanceWithCache(
  fetchData,
  (contractNo, materialNo, packCode, contractPosition) => `${contractNo}-${materialNo}-${packCode}-${contractPosition}`,
);

// TODO - Force BE to fix this on their end?
const transformForExact = (contractPosition: number | null) => (dto: SDDetailsData) => transform(dto, contractPosition);

function transform(dto: SDDetailsData, contractPosition: number | null): ContractDetailsData | null {
  const baseData = {
    contractPeriodEnd: getDate(dto.contractValidTo),
    contractPeriodStart: getDate(dto.contractValidFrom),
    customerContractNumber: dto.poNumber || "",
    schaContractNumber: dto.soNumber || "",
    datePlaced: dto.datePlaced ? new Date(dto.datePlaced) : undefined,
    deliveryAddress: dto.shipToAddress ? transformAddress(dto.shipToAddress) : undefined,
  };
  let sdEntry = undefined;
  if (dto.orderEntries) {
    sdEntry = dto.orderEntries.filter((item) => item.lineNumber === contractPosition)[0];
  }
  if (sdEntry) {
    const availableDeliveries = sdEntry.contractConfirmedLines
      ? sdEntry.contractConfirmedLines.map((item) => ({
          qty: item.confirmedQuantityValue || undefined,
          date: getDate(item.confirmedDeliveryDate),
        }))
      : [{ qty: undefined, date: undefined }];

    const data: ContractDetailsData = {
      ...baseData,
      netPrice: sdEntry.nettoPrice || PriceUtils.zero(),
      totalPrice: sdEntry.priceForTotal || PriceUtils.zero(),
      packageCode: sdEntry.packCode || "",
      packageSize: sdEntry.packSize || 1,
      position: sdEntry.lineNumber,
      availableDeliveries,
      product: transformProduct(sdEntry.product),
      productCode: sdEntry.product.code,
      productDesignation: sdEntry.product.designation,
      openQty: sdEntry.contractOpenQuantity || 0,
      totalQty: sdEntry.quantityRequested || 0,
    };
    return data;
  }
  return null;
}
